import * as yup from 'yup'
import * as cnpj from '@fnando/cnpj'
import { useApi } from '~/store/store'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { AuthActions, trackEvent } from '~/service/analytics'
import React, { Dispatch, ReactNode, SetStateAction, useCallback } from 'react'
import { AuthTemplateView, HfUsernameInput, Badge, LoginSubmitButton, ResetLoading } from '~/components'

type FormValues = {
  username: string
}

type AuthTemplateCNPJFormProps = {
  title: string,
  description: string,
  afterFormChildren?: ReactNode,
  onBackButtonClick?: () => void,
  hideLinkToCreateNewAccount?: boolean,
  setSuccessfulOperation: Dispatch<SetStateAction<boolean>>
}

const urlByEnv: Record<string, string> = {
  alpha: 'https://creditoempresas.alpha.mutual.club/reset',
  production: 'https://creditoempresas.mutual.club/reset',
  borrower: 'https://creditoempresas.borrower.mutual.club/reset',
  development: 'http://localhost:3000/reset'
}

const validationSchema = yup.object().shape({
  username: yup.string().required('Campo obrigatório').test(
    'Valid CNPJ',
    'CNPJ inválido',
    (username) => typeof username === 'string' ? cnpj.isValid(username) : false
  )
})

export const ResetPasswordTokenContainer: React.FC<AuthTemplateCNPJFormProps> = ({
  title,
  description,
  afterFormChildren,
  onBackButtonClick,
  setSuccessfulOperation,
  hideLinkToCreateNewAccount
}) => {
  const [resetPassword, resetPasswordState] = useApi('resetPassword')
  const { loading, error } = resetPasswordState

  const { handleSubmit, control, watch, formState: { isValid, isSubmitted } } = useForm<FormValues>({
    defaultValues: {
      username: ''
    },
    resolver: yupResolver(validationSchema)
  })

  const hasError = isSubmitted && (!isValid || error !== null)

  const username = watch('username')

  const onSubmit: SubmitHandler<FormValues> = useCallback(async () => {
    const env = process.env.PJ_PANEL_ENVIRONMENT ?? 'development'
    const resetPasswordUrl = urlByEnv[env]

    await resetPassword({
      username: cnpj.strip(username),
      baseUrl: resetPasswordUrl
    })

    trackEvent(AuthActions.RECOVER_PASSWORD)
    setSuccessfulOperation(true)
  }, [username])

  if (loading) {
    return <ResetLoading />
  }

  return (
    <AuthTemplateView
      title={title}
      description={description}
      onBackButtonClick={onBackButtonClick}
      hideLinkToCreateNewAccount={hideLinkToCreateNewAccount}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <HfUsernameInput
          acceptCNPJ
          name="username"
          disabled={loading}
          error={hasError}
          control={control}
          removeIcon={false}
          sx={{ marginTop: 4 }}
        />

        {hasError && (
          <Badge
            level="main"
            type="error"
            title="Ops! CNPJ inválido"
            description="Não conseguimos reconhecer o CNPJ informado. Por favor verifique e tente novamente."
            icon="BlockIcon"
            hasError={hasError}
          />
        )}

        <LoginSubmitButton title="Recuperar minha senha" loading={loading} />
      </form>
      {afterFormChildren}
    </AuthTemplateView>
  )
}
